import { useState, useCallback } from 'react';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import classNames from 'classnames';
import { ScrollToTop, Button } from '@crazyegginc/hatch';

import { useSite, usePermissions, useModal, useQueryParams } from '/src/hooks';
import { AccountUsersProvider } from '../account-users-filter-context';
import { SelectionProvider } from '/src/contexts/selection';

import { SEO } from '/src/components/SEO';
import { DashboardPage } from '/src/components/Page';
import { DashHeader } from '/src/components/headers/DashHeader';
import { AddSiteModal } from '/src/components/modals/AddSiteModal';
import { InviteUserModal } from '../components/team/modals/InviteUserModal';
import { SearchInput } from '/src/components/search-input';

import { AccountUsersTeam } from '../components/team/AccountUsersTeam';
import { AccountUsersSharing } from '../components/sharing/AccountUsersSharing';
import { AuditLogDashboard } from '../../audit-log/components/AuditLogDashboard';

import { FEATURES } from '/src/features/_global/constants';

import { ReactComponent as PlusIcon } from '@crazyegginc/hatch/dist/images/icon-plus.svg';

function SearchInputComponent({ placeholderText, ariaLabel, setSearch, search }) {
  const { set: queryParamsSet } = useQueryParams();

  const doSearch = useCallback(
    (value) => {
      setSearch(value);
      queryParamsSet('search', value);
    },
    [queryParamsSet, setSearch],
  );

  return (
    <SearchInput
      search={doSearch}
      inputSize="xl"
      initialValue={search}
      placeholder={placeholderText}
      ariaLabel={ariaLabel}
    />
  );
}

const titlePage = 'Team & Sharing';
const team = 'Manage team';
const sharing = 'Manage sharing';
const auditLog = 'Audit Log';

// eslint-disable-next-line no-restricted-syntax
export default function AccountUsersPage() {
  const modal = useModal();
  const { set: queryParamsSet, get: queryParamsGet, removeAll } = useQueryParams();
  const [search, setSearch] = useState(queryParamsGet('search'));
  const initialTab = queryParamsGet('tab') ?? 0;

  const permissions = usePermissions();
  const canNavigateTeam = permissions.can('navigate', FEATURES.TEAM).allowed;
  const canNavigateSharing = permissions.can('navigate', FEATURES.SHARING).allowed;
  const canNavigateAuditLog = permissions.can('navigate', FEATURES.AUDIT_LOG).allowed;

  const { sites, loadingSites } = useSite({ dontSelectSite: true });

  const siteList = sites.map((site) => site.name);

  const tabs = [];
  if (canNavigateTeam) {
    tabs.push(team);
  }
  if (canNavigateSharing) {
    tabs.push(sharing);
  }
  if (canNavigateAuditLog) {
    tabs.push(auditLog);
  }

  return (
    <DashboardPage>
      <SEO title={titlePage} />

      <TabGroup
        defaultIndex={initialTab}
        onChange={(index) => {
          // clear search input on tab change
          window.dispatchEvent(new Event('filters:reset'));
          removeAll([
            'orderActive',
            'sortActive',
            'orderInactive',
            'sortInactive',
            'pageActive',
            'pageInactive',
            'page',
            'sort',
            'order',
            'search',
          ]);
          setSearch('');
          queryParamsSet('tab', index);
        }}
      >
        {({ selectedIndex }) => (
          <>
            <DashHeader
              className="!border-0"
              title={titlePage}
              actionButton={
                <div className="flex items-center space-x-2.5">
                  {tabs[selectedIndex] && tabs[selectedIndex] !== auditLog && (
                    <span>
                      <SearchInputComponent
                        placeholderText={tabs[selectedIndex] === team ? 'Search members' : 'Search share links'}
                        ariaLabel={tabs[selectedIndex] === team ? 'searchMembers' : 'searchShareLinks'}
                        search={search}
                        setSearch={setSearch}
                      />
                    </span>
                  )}
                  {tabs[selectedIndex] === team ? (
                    <Button
                      disabled={loadingSites}
                      size="xl"
                      onClick={() => {
                        siteList.length === 0
                          ? modal.show(
                              <AddSiteModal
                                numSites={siteList.length}
                                customTitle="Add a site before adding team members"
                                onExtraSteps={() => modal.show(<InviteUserModal />)}
                              />,
                            )
                          : modal.show(<InviteUserModal />);
                      }}
                    >
                      <PlusIcon className="mr-2 h-2.5 w-2.5 fill-current" />
                      Invite members
                    </Button>
                  ) : null}
                </div>
              }
            />

            <TabList className="flex border-b border-mystic-500 bg-white px-10">
              {tabs.map((tabTitle) => (
                <Tab
                  key={tabTitle}
                  className={({ selected }) =>
                    classNames(
                      'text-header-4 border-b-2 px-5 pb-3 text-dodger-blue-500 focus:outline-none focus-visible:outline-black',
                      'flex items-center',
                      {
                        'border-dodger-blue-500': selected,
                        'border-transparent': !selected,
                      },
                    )
                  }
                >
                  {tabTitle}
                </Tab>
              ))}
            </TabList>

            <div className="my-10 px-10">
              <TabPanels>
                {canNavigateTeam ? (
                  <TabPanel>
                    <SelectionProvider>
                      <AccountUsersProvider>
                        {loadingSites || !siteList ? null : <AccountUsersTeam siteList={siteList} search={search} />}
                      </AccountUsersProvider>
                    </SelectionProvider>
                  </TabPanel>
                ) : null}
                {canNavigateSharing ? (
                  <TabPanel>
                    <SelectionProvider>
                      <AccountUsersSharing search={search} setSearch={setSearch} />
                    </SelectionProvider>
                  </TabPanel>
                ) : null}
                {canNavigateAuditLog ? (
                  <TabPanel>
                    <AuditLogDashboard />
                  </TabPanel>
                ) : null}
              </TabPanels>
            </div>
          </>
        )}
      </TabGroup>

      <ScrollToTop />
    </DashboardPage>
  );
}
